/**
 * Konkord: which build of the web app is running (commit and build time,
 * filled in by vite.config.ts) and of the desktop app around it
 */
declare const __KONKORD_BUILD__: { commit: string; date: string } | undefined;

export const webBuild =
  typeof __KONKORD_BUILD__ !== "undefined"
    ? __KONKORD_BUILD__
    : { commit: "dev", date: "" };

type NativeVersions = { desktop?: () => string; built?: () => string };

const nativeVersions = () =>
  (globalThis as { native?: { versions?: NativeVersions } }).native?.versions;

/** Konkord: the desktop app around this page, if any */
export const desktopVersion = () => nativeVersions()?.desktop?.();

function olderVersion(version: string, than: string) {
  const a = version.split(".").map(Number);
  const b = than.split(".").map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) < (b[i] ?? 0);
  }
  return false;
}

/**
 * Konkord: what is too old for our voice server (its /status says the minimum);
 * the site first, since it updates by itself
 */
export function appOutdated(
  min: { minBuild?: unknown; minDesktop?: unknown },
  site: { commit: string; date: string } = webBuild,
  desktop: string | undefined = desktopVersion(),
): "site" | "desktop" | undefined {
  if (site.commit === "dev") return;
  if (
    typeof min.minBuild === "string" &&
    Date.parse(site.date) < Date.parse(min.minBuild)
  )
    return "site";
  if (
    typeof min.minDesktop === "string" &&
    desktop &&
    olderVersion(desktop, min.minDesktop)
  )
    return "desktop";
}

/**
 * "26/10/2026 19:40" in the viewer's time zone, empty when unknown
 */
export function buildDate(iso?: string) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "versão 7dbf87f de 26/10/2026 19:40"
 */
export function webVersionText() {
  const date = buildDate(webBuild.date);
  return `versão ${webBuild.commit}${date ? ` de ${date}` : ""}`;
}

/**
 * "app 0.1.9 de 26/10/2026 19:40" in the desktop app (older apps don't say
 * when they were built), undefined in the browser
 */
export function desktopVersionText() {
  const versions = nativeVersions();
  const version = versions?.desktop?.();
  if (!version) return;
  const date = buildDate(versions?.built?.());
  return `app ${version}${date ? ` de ${date}` : ""}`;
}
